/*
 * Mara League OS — Notifications Full Page (Phase 6)
 * ---------------------------------------------------
 * Complete history + filters + preferences panel.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Bell, Check, Trash2, Trophy, Gavel, UserPlus, Cog, Settings2, ChevronRight, RefreshCw } from "lucide-react";

const CATEGORY_ICON = { match: Trophy, auction: Gavel, registration: UserPlus, platform: Cog };
const CATEGORY_COLOR = {
  match: "text-red-400 border-red-800 bg-red-950/30",
  auction: "text-amber-400 border-amber-800 bg-amber-950/30",
  registration: "text-emerald-400 border-emerald-800 bg-emerald-950/30",
  platform: "text-sky-400 border-sky-800 bg-sky-950/30",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("all");
  const [prefs, setPrefs] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filter === "unread") params.only_unread = true;
      if (category !== "all") params.category = category;
      const [list, prefsResp] = await Promise.all([
        api.get("/notifications", { params }),
        api.get("/notifications/preferences"),
      ]);
      setItems(list.data.items || []);
      setUnread(list.data.unread_total || 0);
      setPrefs(prefsResp.data.prefs);
      setCategories(prefsResp.data.categories);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [filter, category]);

  useEffect(() => { load(); }, [load]);

  const markAll = async () => {
    try {
      const { data } = await api.post("/notifications/mark-read", { all: true });
      toast.success(`${data.modified} marked as read`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markOne = async (id) => {
    try {
      await api.post("/notifications/mark-read", { ids: [id] });
      setItems((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const dismiss = async (id) => {
    try {
      await api.post(`/notifications/dismiss/${id}`);
      setItems((prev) => prev.filter((n) => n.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePref = async (cat, next) => {
    const updated = { ...prefs, [cat]: next };
    setPrefs(updated);
    try {
      await api.put("/notifications/preferences", { prefs: updated });
      toast.success(`${cat} notifications ${next ? "enabled" : "muted"}`);
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const clickItem = async (n) => {
    if (!n.read) await markOne(n.id);
    if (n.url) nav(n.url);
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Your inbox
          </div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white flex items-center gap-3">
            <Bell className="w-10 h-10 text-emerald-400" /> Notifications
          </h1>
          <div className="text-xs text-zinc-400 mt-1">
            Everything that happens across your leagues, matches, and auctions — surfaced here in real time.
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="border-white/10 text-zinc-300">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {unread > 0 && (
            <Button onClick={markAll} className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="page-mark-all-read">
              <Check className="w-4 h-4 mr-1.5" /> Mark all read ({unread})
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="feed" className="w-full">
        <TabsList className="bg-[#11241b] border border-white/10">
          <TabsTrigger value="feed" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Bell className="w-4 h-4 mr-1.5" /> Feed
          </TabsTrigger>
          <TabsTrigger value="prefs" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Settings2 className="w-4 h-4 mr-1.5" /> Preferences
          </TabsTrigger>
        </TabsList>

        <TabsContent value="feed" className="mt-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1">
              {["all", "unread"].map((f) => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`px-3 py-1 rounded text-xs uppercase tracking-widest border ${filter === f ? "border-emerald-600 bg-emerald-950/40 text-emerald-400" : "border-white/10 text-zinc-400 hover:border-white/20"}`}>
                  {f}
                </button>
              ))}
            </div>
            <div className="flex gap-1 ml-2">
              <button onClick={() => setCategory("all")}
                className={`px-3 py-1 rounded text-xs uppercase tracking-widest border ${category === "all" ? "border-white/40 bg-white/5 text-white" : "border-white/10 text-zinc-400 hover:border-white/20"}`}>
                All categories
              </button>
              {categories.map((c) => {
                const active = category === c;
                const Icon = CATEGORY_ICON[c];
                return (
                  <button key={c} onClick={() => setCategory(active ? "all" : c)}
                    className={`px-3 py-1 rounded text-xs uppercase tracking-widest border flex items-center gap-1 ${active ? "border-emerald-500 bg-emerald-950/40 text-emerald-400" : "border-white/10 text-zinc-400 hover:border-white/20"}`}>
                    {Icon && <Icon className="w-3 h-3" />} {c}
                  </button>
                );
              })}
            </div>
          </div>

          <Card className="bg-[#11241b] border-white/10 overflow-hidden">
            {loading ? (
              <div className="p-10 text-center text-zinc-500 text-sm">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center">
                <Bell className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
                <div className="text-zinc-400 text-sm">Nothing here yet.</div>
                <div className="text-zinc-600 text-xs mt-1">Notifications will appear as matches run and platform activity happens.</div>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {items.map((n) => {
                  const Icon = CATEGORY_ICON[n.category] || Bell;
                  const cls = CATEGORY_COLOR[n.category] || "text-white border-white/10 bg-white/5";
                  return (
                    <div key={n.id}
                      className={`px-4 py-3 hover:bg-black/20 transition-colors ${n.read ? "opacity-60" : ""}`}
                      data-testid={`page-notif-${n.id}`}>
                      <div className="flex items-start gap-3">
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-[10px] uppercase tracking-widest ${cls}`}>
                          <Icon className="w-3 h-3" /> {n.category}
                        </div>
                        <button className="flex-1 text-left" onClick={() => clickItem(n)}>
                          <div className="flex items-center gap-2">
                            <div className="text-sm text-white font-medium">{n.title}</div>
                            {!n.read && <Badge className="bg-emerald-600 text-white h-4 px-1.5 text-[9px]">NEW</Badge>}
                            {n.priority === "high" && <Badge className="bg-red-600 text-white h-4 px-1.5 text-[9px]">HIGH</Badge>}
                          </div>
                          {n.body && <div className="text-xs text-zinc-400 mt-0.5">{n.body}</div>}
                          <div className="text-[10px] text-zinc-600 mt-1 flex items-center gap-3">
                            <span>{timeAgo(n.created_at)}</span>
                            <span className="font-mono">{n.event_type}</span>
                          </div>
                        </button>
                        {n.url && (
                          <Button size="sm" variant="ghost" onClick={() => clickItem(n)}
                            className="text-zinc-400 hover:text-white h-8 w-8 p-0">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => dismiss(n.id)}
                          className="text-zinc-500 hover:text-red-400 h-8 w-8 p-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="prefs" className="mt-4 space-y-3">
          <Card className="p-5 bg-[#11241b] border-white/10">
            <h3 className="font-anton text-lg uppercase text-white mb-4">
              Categories you want notifications for
            </h3>
            <div className="space-y-3">
              {categories.map((c) => {
                const Icon = CATEGORY_ICON[c] || Bell;
                const enabled = !!prefs?.[c];
                const desc = {
                  match: "Match kickoffs, finals, Golden Points, and score updates.",
                  auction: "Player announcements, sales, and auction lifecycle.",
                  registration: "Registration approvals, rejections, and new applications.",
                  platform: "Tenant + config changes (admins only see these).",
                }[c];
                return (
                  <div key={c} className="flex items-start gap-3 p-3 rounded border border-white/5 bg-[#0d1c15]"
                    data-testid={`pref-row-${c}`}>
                    <Icon className={`w-5 h-5 mt-0.5 ${CATEGORY_COLOR[c]?.split(" ")[0] || "text-white"}`} />
                    <div className="flex-1">
                      <div className="text-sm text-white font-medium capitalize">{c}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{desc}</div>
                    </div>
                    <Switch checked={enabled} onCheckedChange={(v) => togglePref(c, v)}
                      data-testid={`pref-toggle-${c}`} />
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-zinc-500 mt-4">
              Preferences apply only to <span className="text-white">in-platform</span> notifications you see in the bell + this feed. No SMS, no email, no external messages.
            </p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
